import { I18n } from '@calendly/i18n'
import { groupBy, isEqual, uniq } from 'lodash-es'

import { formatTime, parseTime } from 'services/time_format'

const summarizeInterval = ({ from: intervalStart, to: intervalEnd }, timeNotation) => {
  const from = parseTime(intervalStart)
  const to = parseTime(intervalEnd)

  if (timeNotation === '24h') {
    return `${formatTime(from, timeNotation)} - ${formatTime(to, timeNotation)}`
  }

  const fromMeridian = from.format('a')
  const toMeridian = to.format('a')

  let fromFormat = 'h'
  if (!isEqual(from.format('mm'), '00')) {
    fromFormat += ':mm'
  }
  if (!isEqual(fromMeridian, toMeridian)) {
    fromFormat += ' a'
  }

  let toFormat = 'h'
  if (!isEqual(to.format('mm'), '00')) {
    toFormat += ':mm'
  }
  toFormat += ' a'

  return `${from.format(fromFormat)} - ${to.format(toFormat)}`
}

const summarizeIntervals = (intervals, timeNotation) => {
  if (intervals.length === 0) {
    return I18n.t('publisher.event_type_editor.sections.scheduling_settings.date_overrides.unavailable')
  }

  let additionalIntervals = ''
  if (intervals.length > 1) {
    additionalIntervals = `, +${intervals.length - 1} more time${intervals.length > 2 ? 's' : ''}`
  }

  return `${summarizeInterval(intervals[0], timeNotation)}${additionalIntervals}`
}
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].sort()
const WEEKENDS = ['saturday', 'sunday'].sort()

const summarizeDays = (days) => {
  if (isEqual(days.sort(), WEEKDAYS)) {
    return I18n.t('publisher.event_type_editor.sections.scheduling_settings.summary.weekdays')
  }

  if (isEqual(days.sort(), WEEKENDS)) {
    return I18n.t('publisher.event_type_editor.sections.scheduling_settings.summary.weekends')
  }

  const abbreviatedDayNames = I18n.t('publisher.organization.reporting.abbreviated_day_names')

  return days.map((day) => abbreviatedDayNames[DAYS.indexOf(day)])
    .sort((a, b) => abbreviatedDayNames.indexOf(a) - abbreviatedDayNames.indexOf(b)).join(', ')
}

export const summarizeAvailabilityRule = ({ rules }, timeNotation) => {
  if (!rules?.length) { return '' }

  const rulesThatMatter = rules
    .reduce((prev, curr) => prev.concat(curr), [])
    .filter((rule) => !!rule.wday)
    .map((rule) => rule.intervals.map((int) => ({
      wday: rule.wday,
      key: `${int.from}-${int.to}`,
      interval: int
    })))
    .reduce((prev, curr) => prev.concat(curr), [])

  const intervalMajorData = groupBy(rulesThatMatter, 'key')

  const vals = Object.values(intervalMajorData)
  const mapped = vals.map((val) => val.map((v) => v.wday))

  if (mapped.length === 0) return ''

  if (vals.length === 1 || mapped.every((v) => isEqual(v, mapped[0]))) {
    return `${summarizeDays(vals[0].map((val) => val.wday))}, ${summarizeIntervals(vals.map((val) => val[0].interval), timeNotation)}`
  }

  return `${summarizeDays(uniq(vals.map((val) => val.map((v) => v.wday)).reduce((prev, curr) => prev.concat(curr), [])))}, hours vary`
}
